import type { ReactNode } from "react";
import type { VeterinaryHealthRecord } from "./veterinary-access-api";

const FIELD_LABELS: Record<string, string> = {
  species: "Especie",
  breed: "Raza",
  sex: "Sexo",
  birthDate: "Nacimiento",
  color: "Color",
  size: "Tamaño",
  microchipNumber: "Microchip",
  weightKg: "Peso (kg)",
  allergies: "Alergias",
  conditions: "Condiciones",
  medications: "Medicación informada",
  bloodType: "Grupo sanguíneo",
  notes: "Observaciones",
  criticalAlerts: "Alertas críticas",
  state: "Estado reproductivo",
  procedureAt: "Procedimiento",
  lastHeatAt: "Último celo",
  approximateCycleDays: "Ciclo aproximado (días)",
  pregnancies: "Gestaciones",
  births: "Partos",
  cesareans: "Cesáreas",
  lastLitterAt: "Última camada",
  reproductiveHistory: "Antecedentes reproductivos",
  complications: "Complicaciones",
  activeIngredient: "Principio activo",
  dosage: "Dosis",
  doseValue: "Cantidad",
  doseUnit: "Unidad",
  frequency: "Frecuencia",
  route: "Vía",
  startAt: "Inicio",
  startedAt: "Inicio",
  endAt: "Finalización",
  instructions: "Indicaciones",
  publicCareInstructions: "Advertencias compartibles si el tutor autoriza",
  status: "Estado",
  stoppedAt: "Suspendida el",
  stoppedReason: "Motivo de suspensión",
  prescriberName: "Profesional",
  prescriberLicense: "Matrícula",
  organizationName: "Organización",
  description: "Descripción",
  onsetAt: "Comienzo",
  resolvedAt: "Resolución",
  authorName: "Registrado por",
  authorLicense: "Matrícula",
  type: "Tipo",
  performedAt: "Realizado el",
  dueAt: "Próximo control",
  sourceType: "Origen",
  verifiedAt: "Verificado el",
  administeredAt: "Aplicada el",
  nextDueAt: "Próxima dosis",
  manufacturer: "Fabricante",
  batchNumber: "Lote",
  providerName: "Aplicada por",
  professionalName: "Profesional",
  professionalLicense: "Matrícula",
  reconciledAt: "Conciliada el",
  updatedAt: "Actualizado",
  createdAt: "Registrado",
  emergencyNote: "Información de emergencia",
  reason: "Motivo",
  findings: "Hallazgos",
  anamnesis: "Anamnesis",
  physicalExam: "Examen físico",
  assessment: "Evaluación",
  diagnosis: "Diagnóstico",
  treatment: "Tratamiento",
  indications: "Indicaciones",
  followUpAt: "Seguimiento",
  voidReason: "Motivo de anulación",
  occurredAt: "Fecha",
  note: "Observación",
};
export const VALUE_LABELS: Record<string, string> = {
  DOG: "Perro",
  CAT: "Gato",
  MALE: "Macho",
  FEMALE: "Hembra",
  UNKNOWN: "Sin registrar",
  ACTIVE: "Activo",
  COMPLETED: "Completado",
  DISCONTINUED: "Interrumpido",
  SUSPENDED: "Suspendido",
  CANCELLED: "Cancelado",
  TAKING: "En uso",
  STOPPED: "Suspendido por tutor",
  RECONCILED: "Conciliado",
  RESOLVED: "Resuelto",
  CHRONIC: "Crónico",
  UNDER_FOLLOW_UP: "En seguimiento",
  SIGNED: "Firmada",
  AMENDED: "Con correcciones",
  VOIDED: "Anulada",
  TUTOR_REPORTED: "Informado por tutor",
  VETERINARIAN_RECORDED: "Registrado por veterinario",
  VETERINARIAN_VERIFIED: "Verificado por veterinario",
  IMPORTED: "Importado",
  SYSTEM: "Sistema",
  ORAL: "Oral",
  TOPICAL: "Tópica",
  INJECTABLE: "Inyectable",
  OPHTHALMIC: "Oftálmica",
  OTIC: "Ótica",
  INHALED: "Inhalada",
  UP_TO_DATE: "Al día",
  DUE_SOON: "Próximo a vencer",
  OVERDUE: "Vencido",
  SMALL: "Pequeño",
  MEDIUM: "Mediano",
  LARGE: "Grande",
  VACCINATION: "Vacunación",
  INTERNAL_DEWORMING: "Desparasitación interna",
  EXTERNAL_DEWORMING: "Desparasitación externa",
  GENERAL_CHECKUP: "Control general",
  LABORATORY: "Laboratorio",
  DENTAL: "Control dental",
  OTHER: "Otro",
  INTACT: "Entero/a",
  NEUTERED: "Castrado/a",
  SPAYED: "Esterilizada",
  PREGNANT: "Gestante",
  TAKEN_AS_DIRECTED: "Administrada según indicación",
  MISSED_DOSE: "Dosis omitida",
  STOPPED_BY_TUTOR: "Suspendida por tutor",
  ADMINISTRATION_DIFFICULTY: "Dificultad al administrar",
  ADVERSE_EFFECT: "Efecto adverso informado",
};

export function date(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" });
}

function displayValue(key: string, value: unknown): string {
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value !== "string" && typeof value !== "number") return "";
  if (typeof value === "string" && /(?:At|Date)$/.test(key)) return date(value);
  return VALUE_LABELS[String(value)] ?? String(value);
}

export function HealthFields({
  record,
  fields,
}: {
  record: object | null;
  fields?: string[];
}): ReactNode {
  const values = Object.entries(record ?? {}).filter(
    ([key, value]) =>
      FIELD_LABELS[key] &&
      (!fields || fields.includes(key)) &&
      value !== null &&
      value !== undefined &&
      value !== "" &&
      typeof value !== "object",
  );
  return values.length ? (
    <dl className="vet-access-facts">
      {values.map(([key, value]) => (
        <div key={key}>
          <dt>{FIELD_LABELS[key]}</dt>
          <dd>{displayValue(key, value)}</dd>
        </div>
      ))}
    </dl>
  ) : (
    <p className="muted">Sin información registrada.</p>
  );
}

export function HealthSection({
  title,
  records,
}: {
  title: string;
  records: VeterinaryHealthRecord[];
}): ReactNode {
  return (
    <section className="panel vet-access-section">
      <h2>{title}</h2>
      {records.length ? (
        records.map((record, index) => (
          <article className="vet-access-record" key={record.id ?? index}>
            <h3>{record.name ?? record.title ?? `Registro ${index + 1}`}</h3>
            <HealthFields record={record} />
            {Array.isArray(record.adherenceEvents) && record.adherenceEvents.length > 0 && (
              <details>
                <summary>Seguimiento informado por el tutor</summary>
                {record.adherenceEvents.map((event, eventIndex) => (
                  <HealthFields key={eventIndex} record={event as object} />
                ))}
              </details>
            )}
          </article>
        ))
      ) : (
        <p className="muted">Sin registros.</p>
      )}
    </section>
  );
}
